import React, { useEffect, useState } from 'react';

import { DropdownMenuItem } from 'components/GMetrixButtonDropdown/GMetrixButtonDropdown';
import type { TableFilter } from 'components/GMetrixTable/components/toolbar/components';
import { DetailsButtonItems, GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { Transaction, TransactionApi } from 'apis/TransactionApi';
import { TransactionPermissions } from 'pages/access_codes/types';
import { useNotification } from 'app/notifications';
import { SortDescriptor } from 'utils/dataQuery';
import { UsersApi } from 'apis/UsersApi';
import { useIntl } from 'react-intl';

import 'components/TransactionGMetrixTable/transaction-gmetrix-table.scss';

interface TransactionGMetrixTableProps<T> {
    allowExport?: boolean;
    columnData: GMetrixTableColumnProps<Transaction>[];
    currentUserId: number;
    defaultItemsPerPage?: number;
    detailsButtonItems?: DetailsButtonItems[];
    detailsDropdownItems?: (dataItem: Transaction) => DropdownMenuItem[];
    duplicateButton?: {
        disable?: boolean;
        onClick?: any;
        show: boolean;
    };
    initialSkip?: number;
    loadingGridData: boolean;
    manualLoadGridData?: boolean;
    manuallyRenderTransactions?: boolean;
    multiSelect?: boolean;
    onDetailsItemClick?: (dataItem: Transaction) => Promise<void>;
    onExportAllData?: () => Promise<Transaction[]>;
    onFilterChange?: (filters: { showArchived: boolean; showMyAccessCodes: boolean }) => void;
    onSelectedChange?: (selected: Transaction[]) => void;
    onServerPageChange?: (skip: number, take: number) => void;
    onServerSortChange?: (sort: SortDescriptor[]) => void;
    permissions?: TransactionPermissions;
    searchTerm: string;
    selectableRows?: boolean;
    selectedTransactions?: Transaction[];
    serverSidePagination?: boolean;
    serverTotalCount?: number;
    setLoadingGridData: React.Dispatch<React.SetStateAction<boolean>>;
    setSearchTerm: (searchTerm: string) => void;
    setTransactions: (transactions: Transaction[]) => void;
    toggleColumns?: boolean;
    transactions: Transaction[];
}

export function TransactionGMetrixTable<T>({
    allowExport = true,
    columnData,
    currentUserId,
    defaultItemsPerPage,
    detailsButtonItems,
    detailsDropdownItems,
    initialSkip,
    loadingGridData,
    manuallyRenderTransactions = false,
    multiSelect = true,
    onDetailsItemClick,
    onExportAllData,
    onFilterChange,
    onSelectedChange,
    onServerPageChange,
    onServerSortChange,
    permissions,
    searchTerm,
    selectableRows = true,
    selectedTransactions,
    serverSidePagination = false,
    serverTotalCount,
    setLoadingGridData,
    setSearchTerm,
    setTransactions,
    toggleColumns = true,
    transactions
}: TransactionGMetrixTableProps<T>) {

    const [accessCodeSwitch, setAccessCodeSwitch] = useState(
        permissions?.MyAccessCodes
    );
    const [filteredTransactions, setFilteredTransactions] = useState<
        Transaction[]
    >([]);
    const { formatMessage } = useIntl();
    const { catchWithToast } = useNotification();
    const [showArchivedAccessCodes, setShowArchivedAccessCodes] = useState(false);

    useEffect(() => {

        const myTransactions = sessionStorage.getItem('myTransactions');
        const archivedTransactions = sessionStorage.getItem('archivedTransactions');

        let initialMyAccessCodes = permissions?.MyAccessCodes ?? false;
        let initialShowArchived = false;

        if (myTransactions) {

            initialMyAccessCodes = JSON.parse(myTransactions);

        }
        if (archivedTransactions) {

            initialShowArchived = JSON.parse(archivedTransactions);

        }

        setAccessCodeSwitch(initialMyAccessCodes);
        setShowArchivedAccessCodes(initialShowArchived);

        if (serverSidePagination && onFilterChange) {

            onFilterChange({ showArchived: initialShowArchived, showMyAccessCodes: initialMyAccessCodes });

        }

        if (!manuallyRenderTransactions && !serverSidePagination) {

            TransactionApi.GetAll()
                .then(setTransactions)
                .catch((error: unknown) => catchWithToast(error, formatMessage({ id: 'failed_to_load_access_codes.242fe9e5', defaultMessage: 'Failed to load access codes.' })))
                .finally(() => setLoadingGridData(false));

        }

    }, []);

    useEffect(() => {

        if (serverSidePagination) {

            return;

        }
        let userAccessCodeTransactions = transactions;
        if (accessCodeSwitch) {

            userAccessCodeTransactions = userAccessCodeTransactions.filter(
                transaction => parseInt(transaction.UserId) === currentUserId
            );

        }
        if (!showArchivedAccessCodes) {

            userAccessCodeTransactions = userAccessCodeTransactions.filter(
                transaction => transaction.Archived === false
            );

        }
        setFilteredTransactions(userAccessCodeTransactions);

    }, [accessCodeSwitch, transactions, showArchivedAccessCodes, currentUserId]);

    const handleArchivedChange = (checked: boolean) => {

        setShowArchivedAccessCodes(checked);
        sessionStorage.setItem('archivedTransactions', JSON.stringify(checked));
        if (serverSidePagination && onFilterChange) {

            onFilterChange({ showArchived: checked, showMyAccessCodes: accessCodeSwitch ?? false });

        }

    };

    const handleMyAccessCodesChange = (checked: boolean) => {

        setAccessCodeSwitch(checked);
        sessionStorage.setItem('myTransactions', JSON.stringify(checked));
        UsersApi.SetPagePreference(
            'http://schemas.gmetrix.net/claims/Admin/Preference/PagePreference/MyAccessCodes',
            checked ? 'Show' : 'Hide'
        );
        if (serverSidePagination && onFilterChange) {

            onFilterChange({ showArchived: showArchivedAccessCodes, showMyAccessCodes: checked });

        }

    };

    const filters: TableFilter[] = [
        { checked: showArchivedAccessCodes, label: formatMessage({ id: 'show_archived_access_codes.7f377a54', defaultMessage: 'Show Archived Access Codes' }), onSelect: handleArchivedChange, type: 'checkbox' },
        { checked: accessCodeSwitch ?? false, label: formatMessage({ id: 'show_only_my_access_codes.ae3a039a', defaultMessage: 'Show Only My Access Codes' }), onSelect: handleMyAccessCodesChange, type: 'checkbox' }
    ];

    return (
        <>
            <GMetrixTable
                allowExport={allowExport}
                bottomBarTableName={formatMessage({ id: 'access_codes.3114ae04', defaultMessage: 'Access Codes' })}
                columnData={columnData}
                defaultItemsPerPage={defaultItemsPerPage}
                detailsButtonItems={detailsButtonItems}
                detailsDropdownItems={detailsDropdownItems}
                excelExportFileName={formatMessage({ id: 'accesscodelist.95465887', defaultMessage: 'AccessCodeList' })}
                filters={filters}
                initialSkip={initialSkip}
                loading={loadingGridData}
                multiSelect={multiSelect}
                noDataMessage={formatMessage({ id: 'search_for_an_access_code_to_begin_using.52d01515', defaultMessage: 'Search for an access code to begin using' })}
                onDetailsItemClick={onDetailsItemClick}
                onExportAllData={onExportAllData}
                onSelectedChange={onSelectedChange}
                onServerPageChange={serverSidePagination ? onServerPageChange : undefined}
                onServerSortChange={serverSidePagination ? onServerSortChange : undefined}
                resetPageSignal={`archived:${showArchivedAccessCodes}|mine:${accessCodeSwitch}`}
                rowData={serverSidePagination ? transactions : filteredTransactions}
                searchPlaceholder={formatMessage({ id: 'search_by_name_code_or_student.41e5e507', defaultMessage: 'Search by name, code, or student...' })}
                searchTerm={searchTerm}
                selectableRows={selectableRows}
                selected={selectedTransactions}
                serverSidePagination={serverSidePagination}
                serverTotalCount={serverTotalCount}
                setSearchTerm={setSearchTerm}
                tableName={formatMessage({ id: 'accesscodes.e004cc9b', defaultMessage: 'AccessCodes' })}
                toggleColumns={toggleColumns}

            />
        </>
    );

}
